import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { formatMinutes, formatMinutesDecimal } from './utils';
import { StatusBadge } from './StatusCards';
import { formatLastFirst } from '../../lib/nameFormat';

export const EmployeeSummaryCards = ({ employeeSummary }) => {
  if (employeeSummary.length === 0) {
    return <div className="text-center text-muted-foreground py-12">Keine Daten für diesen Zeitraum</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {employeeSummary.map((emp) => {
        const netMinutes = emp.totalNet || 0;
        const expectedMinutes = emp.expectedMinutes || 1;
        const progressPercent = Math.min(100, Math.round((netMinutes / expectedMinutes) * 100));
        const isOvertime = emp.difference > 0;
        const isDeficit = emp.difference < 0;

        return (
          <Card key={emp.id} className="border-border/60 hover:border-primary/30 transition-colors" data-testid={`card-${emp.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{formatLastFirst(emp)}</p>
                  <p className="text-xs text-muted-foreground">{emp.employee_number}</p>
                </div>
                <StatusBadge status={emp.todayStatus} />
              </div>

              <div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Netto</span>
                  <span className="font-mono font-medium">
                    {formatMinutes(netMinutes)} / {formatMinutes(Math.round(expectedMinutes))}
                  </span>
                </div>
                <Progress value={progressPercent} className="h-2.5" />
                <p className="text-xs text-muted-foreground text-right mt-0.5">{progressPercent}%</p>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Brutto</span>
                  <Tooltip><TooltipTrigger className="cursor-default font-mono">{formatMinutes(emp.totalWork)}</TooltipTrigger>
                    <TooltipContent>{formatMinutesDecimal(emp.totalWork)} Std</TooltipContent></Tooltip>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pausen</span>
                  <Tooltip><TooltipTrigger className="cursor-default font-mono">{formatMinutes(emp.totalBreak)}</TooltipTrigger>
                    <TooltipContent>{formatMinutesDecimal(emp.totalBreak)} Std</TooltipContent></Tooltip>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tage</span>
                  <span className="font-mono">{emp.workDays}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Urlaub</span>
                  <span className="font-mono">{emp.vacationDays > 0 ? emp.vacationDays : '-'}</span>
                </div>
                <div className="flex justify-between col-span-2 border-t pt-1.5 mt-0.5">
                  <span className="text-muted-foreground font-medium">Wochensoll</span>
                  <span className="font-mono font-medium">
                    {(emp.weekly_hours || 40).toFixed(2).replace('.', ',')} h
                  </span>
                </div>
              </div>

              <div className={`flex items-center justify-between p-2 rounded-lg ${
                isOvertime ? 'bg-green-50 dark:bg-green-900/20' : isDeficit ? 'bg-red-50 dark:bg-red-900/20' : 'bg-secondary/30'
              }`}>
                <span className="text-sm font-medium">Differenz</span>
                <span className={`font-mono font-bold ${
                  isOvertime ? 'text-green-600' : isDeficit ? 'text-red-600' : ''
                }`}>
                  {emp.difference >= 0 ? '+' : ''}{formatMinutes(Math.round(emp.difference))}
                </span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
